import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileSignature, Copy, RefreshCw, ExternalLink, ShieldCheck, AlertCircle, FlaskConical, CheckCircle2, XCircle, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PARTNER_PRESETS = [
  { id: "postfinance_yellow_net", name: "PostFinance Yellow Net", url: "https://yellownet.postfinance.ch/api/billing" },
  { id: "abaclik", name: "abaclik (Abacus)", url: "https://www.abaclik.ch/api/ebill/inbound" },
  { id: "aspectra", name: "Aspectra", url: "https://api.aspectra.ch/ebill/v1/invoices" },
  { id: "billinghub", name: "SIX BillingHub", url: "https://billinghub.six-group.com/api/v1/invoice" },
  { id: "custom", name: "Eigener Endpoint", url: "" },
];

export default function Ebill() {
  const { company } = useCompany();
  const [settings, setSettings] = useState(null);
  const [partner, setPartner] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("");
  const [transport, setTransport] = useState("https");
  const [billerId, setBillerId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [testMode, setTestMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const loadDeliveries = useCallback(async () => {
    if (!company?._id) return;
    setDeliveriesLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/ebill/deliveries`, {
        params: { limit: 25 },
      });
      setDeliveries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // silent — delivery log is best-effort
    } finally {
      setDeliveriesLoading(false);
    }
  }, [company?._id]);

  const load = useCallback(async () => {
    if (!company?._id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${company._id}/ebill-settings`);
      setSettings(res.data);
      setPartner(res.data.partner || "");
      setEndpointUrl(res.data.endpoint_url || "");
      setTransport(res.data.transport || "https");
      setBillerId(res.data.biller_id || "");
      setEnabled(!!res.data.enabled);
      setTestMode(res.data.test_mode !== false);
      setApiKey("");
    } catch (e) {
      toast.error("Konnte eBill-Einstellungen nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [company?._id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  const handlePresetChange = (id) => {
    setPartner(id);
    const preset = PARTNER_PRESETS.find((p) => p.id === id);
    if (preset && preset.url && !endpointUrl) setEndpointUrl(preset.url);
  };

  const handleSave = async () => {
    if (!company?._id) return;
    setSaving(true);
    try {
      const body = {
        enabled, partner: partner || null,
        endpoint_url: endpointUrl.trim() || null,
        transport,
        biller_id: billerId.trim() || null,
        test_mode: testMode,
      };
      if (apiKey) body.api_key = apiKey;
      const res = await apiClient.put(`/companies/${company._id}/ebill-settings`, body);
      setSettings(res.data);
      setApiKey("");
      toast.success("eBill-Einstellungen gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const rotateCallback = async () => {
    if (!window.confirm("Neuen Callback-Token generieren? Der alte wird sofort ungültig.")) return;
    try {
      await apiClient.post(`/companies/${company._id}/ebill-settings/rotate-callback-token`);
      toast.success("Neuer Callback-Token generiert.");
      load();
    } catch (e) { toast.error("Rotate fehlgeschlagen."); }
  };

  const [testing, setTesting] = useState(false);
  const handleTestConnection = async () => {
    if (!company?._id) return;
    setTesting(true);
    try {
      const res = await apiClient.post(`/companies/${company._id}/ebill-settings/test-connection`);
      const d = res.data;
      if (d.success) {
        toast.success(`✅ ${d.mode === "mock" ? "Mock" : "Live"}: ${d.message}`);
      } else {
        toast.error(`Verbindungstest fehlgeschlagen: ${d.message}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verbindungstest fehlgeschlagen.");
    } finally { setTesting(false); }
  };

  const copyToClipboard = (text, label = "Kopiert") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const callbackUrl = settings?.callback_token && company?._id
    ? `${BACKEND_URL}/api/ebill/status-callback/${company._id}?token=${settings.callback_token}`
    : "";

  if (loading) {
    return <div className="p-6 text-slate-500 text-sm" data-testid="ebill-loading">Lade…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="ebill-page">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="text-sm text-slate-500 hover:text-slate-900" data-testid="ebill-back-link">
          ← Einstellungen
        </Link>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white shadow-sm">
          <FileSignature className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">eBill-Connector (SIX)</h1>
          <p className="text-sm text-slate-500 mt-1">
            Versende Rechnungen direkt ans Schweizer eBill-Netzwerk. ISO 20022 pain.001-Format,
            kompatibel mit allen Network-Partnern (PostFinance, abaclik, Aspectra, BillingHub).
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={settings?.enabled
            ? "bg-blue-100 text-blue-800 border-blue-200"
            : "bg-slate-100 text-slate-600 border-slate-200"
          } data-testid="ebill-status-badge">
            {settings?.enabled ? "AKTIV" : "INAKTIV"}
          </Badge>
          {settings?.test_mode !== false && (
            <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[10px]">
              <FlaskConical className="h-2.5 w-2.5 mr-0.5" /> TEST-MODUS
            </Badge>
          )}
        </div>
      </div>

      <Card className="p-6 space-y-5 border border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Connector aktivieren</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Wenn aktiv erscheint der "An eBill senden"-Button auf Rechnungen.
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ebill-enabled-switch" />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              Test-Modus
              {testMode && <Badge className="bg-amber-100 text-amber-900 text-[10px]">MOCK</Badge>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Im Test-Modus wird die XML generiert und geloggt, aber NICHT an den Partner gesendet.
              Schalte aus sobald deine Credentials live sind.
            </p>
          </div>
          <Switch checked={testMode} onCheckedChange={setTestMode} data-testid="ebill-test-mode-switch" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">eBill-Partner</label>
            <Select value={partner} onValueChange={handlePresetChange}>
              <SelectTrigger data-testid="ebill-partner-select">
                <SelectValue placeholder="Partner wählen" />
              </SelectTrigger>
              <SelectContent>
                {PARTNER_PRESETS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Bei Auswahl wird die Standard-URL vorbefüllt.</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Transport</label>
            <Select value={transport} onValueChange={setTransport}>
              <SelectTrigger data-testid="ebill-transport-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="https">HTTPS (REST)</SelectItem>
                <SelectItem value="sftp">SFTP (folgt)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Endpoint-URL</label>
          <Input
            type="text"
            placeholder="https://api.partner.ch/ebill/v1/inbound"
            value={endpointUrl}
            onChange={(e) => setEndpointUrl(e.target.value)}
            data-testid="ebill-endpoint-url-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Biller-ID</label>
            <Input
              type="text"
              placeholder="z.B. 41010500000000000"
              value={billerId}
              onChange={(e) => setBillerId(e.target.value)}
              data-testid="ebill-biller-id-input"
            />
            <p className="text-xs text-slate-500">Die vom Partner vergebene Identifikation deiner Firma.</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
              API-Key
              {settings?.has_api_key && (
                <Badge variant="outline" className="text-[11px]">Aktuell: {settings.api_key_preview}</Badge>
              )}
            </label>
            <Input
              type="password"
              placeholder={settings?.has_api_key ? "(leer lassen, um zu behalten)" : "Bearer-Token vom Partner"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="ebill-api-key-input"
            />
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button
            onClick={handleTestConnection}
            disabled={testing || !enabled}
            variant="outline"
            data-testid="ebill-test-connection-btn"
          >
            <FlaskConical className="w-4 h-4 mr-2" />
            {testing ? "Teste…" : "Verbindung testen"}
          </Button>
          <Button onClick={load} variant="outline" disabled={saving} data-testid="ebill-reset-btn">
            Verwerfen
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="ebill-save-btn">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Card>

      {/* Status-Callback URL */}
      <Card className="p-6 border border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-blue-600" />
          <h2 className="font-semibold text-slate-900">Status-Callback (pain.002)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Hinterlege diese URL beim Partner, damit Status-Acks (akzeptiert, abgelehnt, weitergeleitet)
          bei dir landen. Token-authentifiziert — bei Verdacht rotiere ihn.
        </p>
        {callbackUrl ? (
          <div className="flex items-center gap-2">
            <code className="block flex-1 p-2 bg-white rounded font-mono text-xs break-all border border-slate-200">
              {callbackUrl}
            </code>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(callbackUrl, "URL kopiert")} data-testid="ebill-copy-callback-btn">
              <Copy className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={rotateCallback} data-testid="ebill-rotate-callback-btn">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <div className="text-xs text-slate-400">Lade Callback-URL …</div>
        )}
      </Card>

      <Card className="p-6 border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 mb-3">Testen</h2>
        <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
          <li>Aktiviere den Connector + Test-Modus oben → speichern.</li>
          <li>
            Gehe zu{" "}
            <Link to="/invoices" className="text-blue-600 hover:underline" data-testid="ebill-link-invoices">
              Rechnungen
            </Link>{" "}
            → öffne eine Rechnung mit Status "sent" oder "draft" → klicke "An eBill senden".
          </li>
          <li>
            Im Test-Modus erscheint ein Eintrag im Delivery-Log unter{" "}
            <code className="bg-slate-100 px-1 rounded">/api/companies/{company?._id}/ebill/deliveries</code>{" "}
            mit <code>mocked: true</code>.
          </li>
          <li>Sobald deine Credentials live sind: Test-Modus AUS → echte Übermittlung.</li>
        </ol>
        <a
          href="https://www.six-group.com/de/products-services/banking-services/ebill.html"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
        >
          eBill (SIX) Doku öffnen <ExternalLink className="w-3 h-3" />
        </a>
      </Card>

      {/* Delivery-Log */}
      <Card className="p-6 border border-slate-200 bg-white" data-testid="ebill-delivery-log-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-blue-600" />
            <h2 className="font-semibold text-slate-900">Delivery-Log (letzte 25)</h2>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadDeliveries}
            disabled={deliveriesLoading}
            data-testid="ebill-delivery-log-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${deliveriesLoading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
        {deliveries.length === 0 ? (
          <div
            className="text-sm text-slate-500 italic py-6 text-center"
            data-testid="ebill-delivery-log-empty"
          >
            Noch keine Sendungen — sende eine Rechnung an eBill, um den ersten Eintrag zu sehen.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="ebill-delivery-log-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Versendet</th>
                  <th className="py-2 px-2">HTTP</th>
                  <th className="py-2 px-2">Modus</th>
                  <th className="py-2 px-2">Ack</th>
                  <th className="py-2 px-2">Fehler</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr
                    key={d.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`ebill-delivery-row-${d.id}`}
                  >
                    <td className="py-2 px-2">
                      {d.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-700">
                          <XCircle className="w-3.5 h-3.5" /> Fehler
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-slate-600 font-mono text-xs">
                      {d.sent_at ? new Date(d.sent_at).toLocaleString("de-CH") : "—"}
                    </td>
                    <td className="py-2 px-2 text-slate-600">{d.status_code ?? "—"}</td>
                    <td className="py-2 px-2">
                      {d.mocked ? (
                        <Badge className="bg-amber-100 text-amber-900 text-[10px]">MOCK</Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-800 text-[10px]">LIVE</Badge>
                      )}
                    </td>
                    <td className="py-2 px-2 text-slate-600 text-xs">
                      {d.ack_status ? (
                        <Badge variant="outline" className="text-[10px]">{d.ack_status}</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-rose-700 text-xs max-w-[200px] truncate" title={d.error || ""}>
                      {d.error || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
